// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {Analytics} from '../../../../services/analytics';
import {toMongo} from '../../../../util/filters';
import {Banner} from '../../../Banner';
import {
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../Banner/Banner.styles';
import {getDefaultTriggerDescription} from '../utils';
import {isValidActionConfig, useActionConfigStore} from './ActionStore';
import {AutomationStoreActions} from './AutomationStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {isEventValid, useEventStore} from './EventStore';
import {NotificationSection, useSlackIntegrations} from './NotificationSection';
import {
  getDirectionAndAmountString,
  MetricThresholdConfig,
  RunMetricChangeConfig,
  useRunMetricChangeStore,
} from './RunMetricChangeSection';
import {useRunMetricThresholdStore} from './RunMetricThresholdSection';
import {useRunStateStore} from './RunStateSection';
import {AutomationSummarySection, SeparatedChunk} from './shared';

export const DrawerPageBasic = () => {
  const actionEntityName = useActionConfigStore(state => state.entityName);
  const entryPoint = useEventStore(state => state.entryPoint);
  const metricName = useEventStore(
    state => state.initProps.suggestedMetricName
  );
  React.useEffect(() => {
    Analytics.track('Automation Creation', {
      type: 'page_view',
      page: 'basic_config',
      entryPoint,
    });
    // this is just for tracking arrival analytics, so we just want to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    slackIntegrations,
    loading: slackIntegrationsLoading,
    error: slackIntegrationsError,
  } = useSlackIntegrations(actionEntityName);

  return (
    <Tailwind>
      <SeparatedChunk>
        <Banner variant="info">
          <BannerFlexWrapper>
            <Icon name="lightbulb-info" />
            <BannerTextWrapper>
              <div>
                Get notified on Slack when <b>{metricName}</b> meets a threshold
              </div>
            </BannerTextWrapper>
          </BannerFlexWrapper>
        </Banner>
      </SeparatedChunk>
      <MetricThresholdConfig />
      <NotificationSection
        integrations={slackIntegrations}
        slackIntegrationsLoading={slackIntegrationsLoading}
        slackIntegrationsError={slackIntegrationsError}
      />
      <SeparatedChunk>
        <Button
          onClick={() => {
            AutomationStoreActions.switchToAdvancedConfig();
            Analytics.track('Automation Creation', {
              type: 'click_advanced_config',
              entryPoint: 'create_trigger_drawer',
            });
          }}
          variant="secondary">
          Advanced configuration
        </Button>
      </SeparatedChunk>
    </Tailwind>
  );
};

export const BasicPageFooter = ({
  createTrigger,
}: {
  createTrigger: (triggerName: string) => void;
}) => {
  const eventConfig = useEventStore();
  const actionConfig = useActionConfigStore();
  const runMetricConfig = useRunMetricThresholdStore();
  const runMetricChangeConfig = useRunMetricChangeStore();
  const runStateConfig = useRunStateStore();
  const notificationConfig = actionConfig.notification;

  const descriptionDict = getDefaultTriggerDescription(
    {
      eventType: eventConfig.eventType,
      triggerScopeType: eventConfig.scopeType,
      entityName: eventConfig.entityName,
      projectName: eventConfig.projectName,
      artifactCollectionName: eventConfig.scopeArtifactCollectionName,
      filters: JSON.stringify(toMongo(eventConfig.filters)),
      runMetricConfig,
      runMetricChangeConfig,
      notificationConfig,
    },
    actionConfig.actionType,
    actionConfig.job.queuedJob?.name ?? '',
    actionConfig.job.queuedJobAlias ?? '',
    actionConfig.webhook.webhook?.name,
    actionConfig.notification.teamSlackIntegration?.channelName
  );

  const canCreateTrigger =
    isEventValid(
      eventConfig,
      runMetricConfig,
      runMetricChangeConfig,
      runStateConfig
    ) && isValidActionConfig(actionConfig);

  return (
    <div className="flex w-full flex-col">
      <div
        className="bg-moon-100 pb-16 pt-16"
        style={{marginLeft: '-20px', marginRight: '-20px'}}>
        <div style={{marginLeft: '20px', marginRight: '20px'}}>
          <div className="text-lg font-semibold text-moon-500">
            Automation Summary
          </div>
          <AutomationSummarySection
            eventTitle={descriptionDict.event.title}
            eventBody={descriptionDict.event.body}
            actionTitle={descriptionDict.action.title}
            actionBody={descriptionDict.action.body}
          />
        </div>
      </div>
      <S.Divider></S.Divider>
      <Button
        variant="primary"
        size="large"
        className="mt-16 w-full"
        onClick={() => {
          if (!runMetricChangeConfig.metricFilter.metricName) {
            // this should never happen - we should always have a metric name
            throw new Error('Metric name is required');
          }
          createTrigger(
            getTriggerName(
              runMetricChangeConfig.metricFilter.metricName,
              runMetricChangeConfig
            )
          );
        }}
        disabled={!canCreateTrigger}>
        Create
      </Button>
    </div>
  );
};

// basic page only supports metric change triggers
const getTriggerName = (
  metricName: string,
  metricChangeConfig: RunMetricChangeConfig
) => {
  const {changeDirectionStr, changeAmountStr} = getDirectionAndAmountString(
    metricChangeConfig.metricFilter
  );
  return `${metricName} ${changeDirectionStr} ${changeAmountStr} alert`;
};
